// @ts-check

/**
 * @file Renders the timelog dashboard log-time modal.
 */

import Text from "@kaspernj/api-maker/build/utils/text"
import {memo, useEffect} from "react"
import PropTypes from "prop-types"
import PropTypesExact from "prop-types-exact"
import {View} from "react-native-propforge"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"

import Button from "../../../components/button"
import DurationInput from "../../../components/duration-input"
import Modal from "../../../components/modal"
import TasksSelect from "../../../components/tasks/select"
import TextInput from "../../../components/text-input"
import UsersSelect from "../../../components/users/select"
import useThemeColors, {useThemeName} from "../../../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} TimelogsDashboardLogTimeSubmitValues
 * @property {string} comment Comment text.
 * @property {string} date Work date.
 * @property {string} hours Hour input text.
 * @property {string} taskId Selected task identifier.
 * @property {string} userId Selected user identifier.
 */

/**
 * @typedef {object} TimelogsDashboardLogTimeModalProps
 * @property {boolean} canPickUser When false, the user picker is hidden and the timelog is always attributed to `initialUserId` (used for non-admins who can only log their own time).
 * @property {string} initialComment Initial comment text.
 * @property {string} initialDate Initial work date.
 * @property {string} initialHours Initial hour input text.
 * @property {string} initialTaskId Initial selected task id.
 * @property {string} initialUserId Initial selected user id.
 * @property {boolean} isSaving Whether submission is in progress.
 * @property {boolean} isVisible Whether the modal is visible.
 * @property {"create" | "edit"} mode Modal mode.
 * @property {() => unknown} onClose Close callback.
 * @property {(values: TimelogsDashboardLogTimeSubmitValues) => unknown} onSubmit Submit callback.
 * @property {string} [statusText] Optional status message.
 */

/**
 * @typedef {object} TimelogsDashboardLogTimeModalState
 * @property {string} comment Draft comment text.
 * @property {string} date Draft work date.
 * @property {string} hours Draft hour input text.
 * @property {string} selectedTaskId Task option currently chosen in the form.
 * @property {string} selectedUserId User option currently chosen in the form.
 */

/**
 * Displays the create/edit timelog modal with shared async model selects.
 * @augments {ShapeComponent<TimelogsDashboardLogTimeModalProps, TimelogsDashboardLogTimeModalState>}
 */
class TimelogsDashboardLogTimeModal extends ShapeComponent {
  /** @type {TimelogsDashboardLogTimeModalState} */
  state = {
    comment: "",
    date: "",
    hours: "",
    selectedTaskId: "",
    selectedUserId: ""
  }
  static propTypes = PropTypesExact({
    canPickUser: PropTypes.bool.isRequired,
    initialComment: PropTypes.string.isRequired,
    initialDate: PropTypes.string.isRequired,
    initialHours: PropTypes.string.isRequired,
    initialTaskId: PropTypes.string.isRequired,
    initialUserId: PropTypes.string.isRequired,
    isSaving: PropTypes.bool.isRequired,
    isVisible: PropTypes.bool.isRequired,
    mode: PropTypes.oneOf(["create", "edit"]).isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    statusText: PropTypes.string
  })

  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /**
   * Synchronizes draft form fields when the modal opens.
   * @returns {void} No return value.
   */
  setup() {
    useLocale()
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    const {
      initialComment,
      initialDate,
      initialHours,
      initialTaskId,
      initialUserId,
      isVisible
    } = this.p
    useEffect(() => {
      if (!isVisible) return
      this.resetDraftFromProps()
    }, [
      initialComment,
      initialDate,
      initialHours,
      initialTaskId,
      initialUserId,
      isVisible
    ])
  }

  /**
   * Renders the log-time form modal.
   * @returns {ReactNode} Modal content.
   */
  render() {
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    return (
      <Modal
        cardStyle={styles.cardSpacing ||= {
          gap: 16,
          maxWidth: 680,
          padding: 20
        }}
        isVisible={this.p.isVisible}
        onRequestClose={this.p.onClose}
        testID="timelogsDashboardScreen/logTimeModal"
      >
          <Text
            style={styles[`title-${themeName}`] ||= {
              fontSize: 20,
              fontWeight: 700
            }}
            testID="timelogsDashboardScreen/logTimeModal/title"
          >
            {this.p.mode === "edit" ? _("Edit timelog") : _("Log time")}
          </Text>
          <View
            style={styles.form ||= {
              gap: 12
            }}
            testID="timelogsDashboardScreen/logTimeModal/form"
          >
            <View
              style={styles.fieldsGrid ||= {
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 16
              }}
              testID="timelogsDashboardScreen/logTimeModal/fieldsGrid"
            >
              <View
                style={styles.halfField ||= {
                  flexBasis: "calc(50% - 8px)",
                  flexGrow: 1,
                  minWidth: 220
                }}
                testID="timelogsDashboardScreen/logTimeModal/hoursField"
              >
                <DurationInput
                  label={_("Hours")}
                  onChangeText={this.tt.onHoursChangeText}
                  testID="timelogsDashboardScreen/logTimeModal/hoursInput"
                  value={this.s.hours}
                />
              </View>
              <View
                style={styles.halfField}
                testID="timelogsDashboardScreen/logTimeModal/dateField"
              >
                <Text
                  style={styles[`inputLabel-${themeName}`] ||= {
                    color: colors.textMuted,
                    fontWeight: 600
                  }}
                  testID="timelogsDashboardScreen/logTimeModal/dateInput/label"
                >
                  {_("Date")}
                </Text>
                <TextInput
                  onChangeText={this.tt.onDateChangeText}
                  style={styles.inputSpacing ||= {
                    fontSize: 16,
                    marginTop: 6
                  }}
                  testID="timelogsDashboardScreen/logTimeModal/dateInput"
                  value={this.s.date}
                />
              </View>
            </View>
            <View
              style={styles.optionSection ||= {
                gap: 8
              }}
              testID="timelogsDashboardScreen/logTimeModal/taskSection"
            >
              <Text
                style={styles[`label-${themeName}`] ||= {
                  color: colors.textMuted,
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: "uppercase"
                }}
                testID="timelogsDashboardScreen/logTimeModal/taskLabel"
              >
                {_("Task")}
              </Text>
              <View testID="timelogsDashboardScreen/logTimeModal/taskSelect">
                <TasksSelect
                  includeProjectName
                  onChangeValue={this.tt.onTaskValueChange}
                  placeholder={_("Search tasks...")}
                  testID="timelogsDashboardScreen/logTimeModal/taskSelect/select"
                  values={this.s.selectedTaskId ? [this.s.selectedTaskId] : []}
                />
              </View>
            </View>
            {this.p.canPickUser &&
              <View
                style={styles.optionSection}
                testID="timelogsDashboardScreen/logTimeModal/userSection"
              >
                <Text
                  style={styles[`label-${themeName}`]}
                  testID="timelogsDashboardScreen/logTimeModal/userLabel"
                >
                  {_("User")}
                </Text>
                <View testID="timelogsDashboardScreen/logTimeModal/userSelect">
                  <UsersSelect
                    onChangeValue={this.tt.onUserValueChange}
                    placeholder={_("Search users...")}
                    testID="timelogsDashboardScreen/logTimeModal/userSelect/select"
                    values={this.s.selectedUserId ? [this.s.selectedUserId] : []}
                  />
                </View>
              </View>
            }
            <View
              style={styles.commentField ||= {
                gap: 6
              }}
              testID="timelogsDashboardScreen/logTimeModal/commentField"
            >
              <Text
                style={styles[`inputLabel-${themeName}`]}
                testID="timelogsDashboardScreen/logTimeModal/commentInput/label"
              >
                {_("Comment")}
              </Text>
              <TextInput
                multiline
                numberOfLines={3}
                onChangeText={this.tt.onCommentChangeText}
                style={styles.textAreaSpacing ||= {fontSize: 16, marginTop: 6}}
                testID="timelogsDashboardScreen/logTimeModal/commentInput"
                value={this.s.comment}
              />
            </View>
            {this.p.statusText &&
              <Text
                style={styles[`errorText-${themeName}`] ||= {
                  color: colors.dangerText,
                  fontWeight: 600
                }}
                testID="timelogsDashboardScreen/logTimeModal/statusText"
              >
                {this.p.statusText}
              </Text>
            }
            <View
              style={styles.actions ||= {
                flexDirection: "row",
                gap: 10,
                justifyContent: "flex-end",
                marginTop: 4
              }}
              testID="timelogsDashboardScreen/logTimeModal/actions"
            >
              <Button
                disabled={this.p.isSaving}
                iconName="xmark"
                label={_("Cancel")}
                onPress={this.p.onClose}
                testID="timelogsDashboardScreen/logTimeModal/cancelButton"
                variant="outline"
              />
              <Button
                disabled={this.p.isSaving}
                iconName="floppy-disk"
                label={this.p.isSaving ? _("Saving...") : _("Save")}
                onPress={this.tt.onSavePress}
                testID="timelogsDashboardScreen/logTimeModal/saveButton"
              />
            </View>
          </View>
      </Modal>
    )
  }

  /**
   * Resets draft fields from the current modal props. Selection ids
   * carry over verbatim so the shared selects resolve them through their
   * `values` callback even if they're outside the first page.
   * @returns {void} No return value.
   */
  resetDraftFromProps() {
    this.s.comment = this.p.initialComment
    this.s.date = this.p.initialDate
    this.s.hours = this.p.initialHours
    this.s.selectedTaskId = this.p.initialTaskId
    this.s.selectedUserId = this.p.initialUserId
  }

  /**
   * Stores the draft comment.
   * @param {string} value Next free-form work note.
   * @returns {void} No return value.
   */
  onCommentChangeText = (value) => {
    this.s.comment = value
  }

  /**
   * Stores the draft date.
   * @param {string} value Next work date input.
   * @returns {void} No return value.
   */
  onDateChangeText = (value) => {
    this.s.date = value
  }

  /**
   * Stores the draft hours.
   * @param {string} value Next duration input.
   * @returns {void} No return value.
   */
  onHoursChangeText = (value) => {
    this.s.hours = value
  }

  /**
   * Updates the selected task id from the shared task select.
   * @param {(string | number)[] | string | number | undefined} value Selected id payload.
   * @returns {void} No return value.
   */
  onTaskValueChange = (value) => {
    this.s.selectedTaskId = selectedIdFromSelectValue(value)
  }

  /**
   * Updates the selected user id from the shared user select.
   * @param {(string | number)[] | string | number | undefined} value Selected id payload.
   * @returns {void} No return value.
   */
  onUserValueChange = (value) => {
    this.s.selectedUserId = selectedIdFromSelectValue(value)
  }

  /**
   * Submits the current draft values.
   * @returns {void} No return value.
   */
  onSavePress = () => {
    this.p.onSubmit({
      comment: this.s.comment,
      date: this.s.date,
      hours: this.s.hours,
      taskId: this.s.selectedTaskId,
      userId: this.s.selectedUserId
    })
  }
}

/**
 * Normalizes a shared model select payload into one selected id.
 * @param {(string | number)[] | string | number | undefined} value Select callback payload.
 * @returns {string} Selected id or empty string.
 */
function selectedIdFromSelectValue(value) {
  if (Array.isArray(value)) {
    return value.length > 0 ? String(value[0]) : ""
  }
  if (typeof value === "string" || typeof value === "number") {
    return String(value)
  }
  return ""
}

/** @type {NamedExoticComponent<TimelogsDashboardLogTimeModalProps>} */
const TimelogsDashboardLogTimeModalComponent = memo(shapeComponent(TimelogsDashboardLogTimeModal))

export default TimelogsDashboardLogTimeModalComponent
